import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { demoDataGenerator } from '../../utils/demoDataGenerator';

const PreviewAuditLogs = () => {
  const navigate = useNavigate();
  const [selectedEventType, setSelectedEventType] = useState('all');
  
  const demoData = useMemo(() => demoDataGenerator?.getAuditLogs(), []);
  const { logs, stats } = demoData;

  const filteredLogs = selectedEventType === 'all'
    ? logs
    : logs?.filter(log => log?.eventType === selectedEventType);

  const eventTypes = [
    { id: 'all', name: 'All Events', icon: 'Activity' },
    { id: 'treatment_applied', name: 'Treatments', icon: 'Droplet' },
    { id: 'compliance_violation', name: 'Compliance', icon: 'Shield' },
    { id: 'user_login', name: 'User Activity', icon: 'User' },
    { id: 'data_export', name: 'Data Export', icon: 'Download' },
    { id: 'settings_changed', name: 'Settings', icon: 'Settings' }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'info': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date?.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Preview Banner */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Eye" size={24} />
              <div>
                <h3 className="font-semibold text-lg">Preview Mode - Audit Log Dashboard</h3>
                <p className="text-sm text-indigo-100">Sign up to track all activity and maintain compliance records</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="bg-white text-indigo-600 hover:bg-indigo-50 border-white"
                onClick={() => navigate('/pricing-page')}
              >
                View Plans
              </Button>
              <Button
                className="bg-purple-600 hover:bg-purple-700 text-white"
                onClick={() => navigate('/login')}
              >
                Sign Up Free
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Audit Log Dashboard</h1>
          <p className="text-gray-600 mt-2">Complete activity tracking and compliance audit trail</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Activity" size={20} className="text-indigo-600" />
              <span className="text-sm text-gray-600">Total Events</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats?.totalEvents?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Clock" size={20} className="text-blue-600" />
              <span className="text-sm text-gray-600">Today</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{stats?.todayEvents}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertTriangle" size={20} className="text-red-600" />
              <span className="text-sm text-gray-600">Critical</span>
            </div>
            <div className="text-2xl font-bold text-red-600">{stats?.criticalEvents}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="AlertCircle" size={20} className="text-yellow-600" />
              <span className="text-sm text-gray-600">Warnings</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">{stats?.warningEvents}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Info" size={20} className="text-blue-600" />
              <span className="text-sm text-gray-600">Info</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">{stats?.infoEvents?.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="Users" size={20} className="text-purple-600" />
              <span className="text-sm text-gray-600">Users</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">{stats?.uniqueUsers}</div>
          </div>
        </div>

        {/* Event Type Filters */}
        <div className="mb-6 flex gap-2 overflow-x-auto pb-2">
          {eventTypes?.map(type => (
            <button
              key={type?.id}
              onClick={() => setSelectedEventType(type?.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                selectedEventType === type?.id
                  ? 'bg-indigo-600 text-white' :'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              <Icon name={type?.icon} size={18} />
              {type?.name}
            </button>
          ))}
        </div>

        {/* Audit Log Timeline */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Activity Timeline</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {filteredLogs?.map((log, index) => (
              <div key={log?.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-4">
                  {/* Timeline Indicator */}
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      log?.severity === 'critical' ? 'bg-red-100' :
                      log?.severity === 'warning'? 'bg-yellow-100' : 'bg-blue-100'
                    }`}>
                      <Icon
                        name={eventTypes?.find(t => t?.id === log?.eventType)?.icon || 'Activity'}
                        size={20}
                        className={`${
                          log?.severity === 'critical' ? 'text-red-600' :
                          log?.severity === 'warning'? 'text-yellow-600' : 'text-blue-600'
                        }`}
                      />
                    </div>
                    {index < filteredLogs?.length - 1 && (
                      <div className="w-0.5 h-full bg-gray-200 mt-2" />
                    )}
                  </div>

                  {/* Event Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{log?.action}</h3>
                          <span className={`px-2 py-1 text-xs rounded-full ${getSeverityColor(log?.severity)}`}>
                            {log?.severity}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <span className="flex items-center gap-1">
                            <Icon name="User" size={14} />
                            {log?.user}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Clock" size={14} />
                            {formatTimestamp(log?.timestamp)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Icon name="Monitor" size={14} />
                            {log?.device}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Event Details */}
                    {log?.details && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm space-y-1">
                          {Object.entries(log?.details)?.map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <span className="text-gray-600 capitalize">{key?.replace(/([A-Z])/g, ' $1')}:</span>
                              <span className="text-gray-900 font-medium">{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* IP Address */}
                    {log?.ipAddress !== 'N/A' && (
                      <div className="mt-2 text-xs text-gray-500">
                        IP: {log?.ipAddress}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-8 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-8 border-2 border-indigo-200">
          <div className="text-center max-w-2xl mx-auto">
            <Icon name="Shield" size={48} className="text-indigo-600 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Complete Audit Trail & Compliance</h3>
            <p className="text-gray-600 mb-6">
              Sign up to maintain a complete audit trail of all activities in your operation. Perfect for compliance audits, security monitoring, and operational transparency.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                onClick={() => navigate('/login')}
              >
                Start Free Trial
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-indigo-600 text-indigo-600 hover:bg-indigo-50"
                onClick={() => navigate('/pricing-page')}
              >
                View All Features
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-4">✓ Complete activity logs • ✓ Compliance reports • ✓ Security monitoring • ✓ Export capabilities</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreviewAuditLogs;